import { useState } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { aiAssistant } from '@/lib/aiApi';

const ACTION_LABEL = {
  delete_pages: 'Delete pages',
  extract_pages: 'Extract pages',
  merge_split: 'Split into groups',
  compress: 'Compress',
  convert_to_image: 'Convert to image',
  extract_text: 'Extract text',
  extract_table: 'Extract table',
  answer: 'Answer',
  no_action: 'No action',
};

// Map action.type → the button label users see on Apply. Tool-pre-fill
// actions read as "Open in <Tool>" so the user knows what'll happen next.
const APPLY_BUTTON_LABEL = {
  delete_pages: 'Delete pages',
  extract_pages: 'Extract pages',
  merge_split: 'Open in Split',
  compress: 'Open in Compress',
  convert_to_image: 'Open in Convert',
};

export default function AssistantTab({ file, documentHash, onAction }) {
  const [instruction, setInstruction] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    if (!instruction.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const data = await aiAssistant(documentHash
        ? { documentHash, instruction: instruction.trim() }
        : { file, instruction: instruction.trim() });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Assistant failed.');
    } finally {
      setBusy(false);
    }
  }

  const canApply = result && result.action && result.action.type !== 'no_action' && result.action.type !== 'answer';

  return (
    <div className="p-4 overflow-y-auto h-full">
      <label className="block text-sm">
        <span className="text-gray-700 font-medium">What do you want to do?</span>
        <textarea
          rows={3}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder='e.g. "Delete pages 3-5" or "Summarize the Q3 section"'
          className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm resize-y"
        />
      </label>
      <button
        type="button"
        onClick={run}
        disabled={busy || !instruction.trim()}
        className="mt-3 w-full rounded-lg bg-indigo-600 text-white py-2 text-sm font-semibold hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center justify-center gap-1"
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</> : <><Wand2 className="w-4 h-4" /> Ask assistant</>}
      </button>
      {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
      {result && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-2">
          <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">
            {ACTION_LABEL[result.action?.type] || result.action?.type}
          </div>
          <p className="text-gray-800 whitespace-pre-wrap">{result.explanation}</p>
          {result.action?.pages?.length > 0 && (
            <div className="text-xs text-gray-600">Pages: {result.action.pages.join(', ')}</div>
          )}
          {result.answer && (
            <div className="mt-2 text-sm text-gray-900 whitespace-pre-wrap">{result.answer}</div>
          )}
          {canApply && onAction && (
            <button
              type="button"
              onClick={() => onAction(result.action)}
              className="mt-2 w-full rounded-lg bg-gray-900 text-white py-1.5 text-xs font-semibold hover:bg-gray-800"
            >
              {APPLY_BUTTON_LABEL[result.action.type] || 'Apply this action'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
